import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { ApiError, api } from "@/lib/api";
import { pragueWallToIso } from "@/lib/datetime";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

/**
 * `/time-entries/new` — log a manual time entry against a work
 * item. The entry is pushed to Clockify (the source of truth)
 * and mirrored into Hub by the server write-through.
 *
 * `?project=` (slug or cuid) pre-selects the project picker —
 * the project-scoped time-entries page passes it.
 */

const searchSchema = z.object({ project: z.string().optional() });

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type Project = { id: string; name: string; slug: string };
type WorkItem = {
  id: string;
  title: string;
  gitlabIssueId: string;
  globalId: number;
  state?: "open" | "closed";
};
type Overhead = { id: string; name: string; isDefault: boolean };

export const Route = createFileRoute("/_app/time-entries/new")({
  validateSearch: searchSchema,
  loader: () => api.get<{ projects: Project[] }>("/api/projects?pageSize=200"),
  component: NewTimeEntryPage,
});

function NewTimeEntryPage() {
  const { projects } = Route.useLoaderData();
  const { user: appUser } = useLoaderData({ from: "/_app" });
  const isAdmin = appUser.globalRole === "admin";
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Resolve the `?project=` slug-or-cuid against the loaded list.
  const preselected = search.project
    ? projects.find((p) => p.slug === search.project || p.id === search.project)
    : undefined;

  const [projectId, setProjectId] = useState(preselected?.id ?? "");
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  // Default: open WIs only. Admins can flip to include closed
  // when attaching a TE to an already-finished item.
  const [showClosed, setShowClosed] = useState(false);
  const [overheads, setOverheads] = useState<Overhead[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // Unified target picker — work items + overhead categories live in
  // one combobox. The value is prefixed (`wi:<id>` / `oh:<id>`) so the
  // submit handler knows whether to POST `workItemId` or `overheadId`.
  const [target, setTarget] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Optional start-of-day clock time, pre-filled to 09:00 (Prague
  // wall time). The user can tweak it; left as-is it just anchors
  // the synthesized interval so the listing's Start column isn't a
  // bare date.
  const [startTime, setStartTime] = useState("09:00");
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");
  const createEntry = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/time-entries", body),
    {
      onSuccess: () => navigate({ to: "/time-entries" }),
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't create the time entry — Clockify may not be connected (set your API key on Profile)."
          : "Couldn't create the time entry.",
    },
  );

  // Load the project's work items + overhead categories whenever
  // the project changes. Admin viewers also get closed WIs (the
  // server only honours `includeClosed=true` for admin sessions),
  // so they can attach a TE to a finished item.
  useEffect(() => {
    if (!projectId) {
      setWorkItems([]);
      setOverheads([]);
      setTarget("");
      return;
    }
    let cancelled = false;
    setTarget("");
    setLoadingItems(true);
    Promise.all([
      api.get<WorkItem[]>(
        `/api/work-items?projectId=${projectId}&pageSize=200${isAdmin ? "&includeClosed=true" : ""}`,
      ),
      api.get<Overhead[]>(`/api/projects/${projectId}/overheads`),
    ])
      .then(([items, ovs]) => {
        if (cancelled) return;
        setWorkItems(Array.isArray(items) ? items : []);
        setOverheads(Array.isArray(ovs) ? ovs : []);
      })
      .catch(() => {
        if (cancelled) return;
        setWorkItems([]);
        setOverheads([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, isAdmin]);

  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const h = Number.parseFloat(hours);
    if (!projectId || !date || !h || h <= 0 || !target) {
      createEntry.setError(
        "Project, a work item or overhead, date and a positive hours value are required.",
      );
      return;
    }
    // `target` is prefixed: `wi:<id>` for work items, `oh:<id>` for
    // overhead categories. Server takes one or the other.
    const isWi = target.startsWith("wi:");
    const id = target.slice(3);
    const workItem = isWi ? workItems.find((w) => w.id === id) : undefined;
    const overhead = !isWi ? overheads.find((o) => o.id === id) : undefined;
    // Default description: WI ref+title for a WI entry; overhead's
    // name for an overhead entry; otherwise the user's note.
    const finalDescription =
      description.trim() ||
      (workItem ? `#${workItem.globalId} ${workItem.title}` : (overhead?.name ?? "Overhead"));

    // Start = picked date + start time (Prague wall → ISO). Empty
    // start time falls back to 09:00. End = start + hours.
    const start = new Date(pragueWallToIso(`${date}T${startTime || "09:00"}`));
    const end = new Date(start.getTime() + h * 60 * 60 * 1000);

    await createEntry.mutate({
      ...(workItem ? { workItemId: workItem.id } : { overheadId: id }),
      description: finalDescription,
      start: start.toISOString(),
      end: end.toISOString(),
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">New Time Entry</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Log time against a work item. Syncs to Clockify.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/time-entries">Cancel</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Entry Details</CardTitle>
          <CardDescription>
            Select a project and work item, then enter the time spent.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium leading-none block">
                Project <span className="text-destructive">*</span>
              </span>
              <Combobox
                value={projectId}
                onChange={setProjectId}
                placeholder="Select a project..."
                emptyLabel="No projects"
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium leading-none block">
                Work item or overhead <span className="text-destructive">*</span>
              </span>
              <Combobox
                value={target}
                onChange={setTarget}
                disabled={!projectId || loadingItems}
                loading={loadingItems}
                placeholder={
                  !projectId
                    ? "Select a project first"
                    : loadingItems
                      ? "Loading…"
                      : "Pick a work item or overhead…"
                }
                emptyLabel="No work items or overheads — add some in project settings"
                // Work items first (most-used path), then overheads.
                // The `hint` column on the right disambiguates visually
                // so a user typing "Misc" sees `Overhead` next to it
                // and knows what they picked. Value is prefixed so the
                // submit handler routes to the right API field.
                options={[
                  ...workItems.map((wi) => ({
                    value: `wi:${wi.id}`,
                    label: `#${wi.globalId} ${wi.title}`,
                    hint: "Work item",
                    // Recently-closed WIs stay assignable (grace window) —
                    // struck-through so it's clear they're finished.
                    struck: wi.state === "closed",
                  })),
                  ...overheads.map((o) => ({
                    value: `oh:${o.id}`,
                    label: o.isDefault ? `${o.name} (default)` : o.name,
                    hint: "Overhead",
                  })),
                ]}
              />
              {isAdmin && (
                <label className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={showClosed}
                    onChange={(e) => setShowClosed(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-input"
                  />
                  Show closed work items
                </label>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label htmlFor="date" className="text-sm font-medium leading-none">
                  Date <span className="text-destructive">*</span>
                </label>
                <DatePicker id="date" required value={date} onChange={setDate} />
              </div>
              <div className="space-y-2">
                <label htmlFor="start-time" className="text-sm font-medium leading-none">
                  Start <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="hours" className="text-sm font-medium leading-none">
                  Hours <span className="text-destructive">*</span>
                </label>
                <input
                  id="hours"
                  type="number"
                  required
                  min="0.25"
                  step="0.25"
                  placeholder="1.00"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-none">
                Description
              </label>
              <input
                id="description"
                type="text"
                placeholder="What did you work on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={INPUT_CLS}
              />
            </div>

            {createEntry.error && <p className="text-sm text-destructive">{createEntry.error}</p>}

            <div className="flex gap-3">
              <Button type="submit" disabled={createEntry.pending}>
                {createEntry.pending ? "Creating…" : "Create Time Entry"}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/time-entries">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
